import React, { useEffect, useState } from 'react';
import { Modal, Descriptions, Table, Tag, Tabs, Statistic, Row, Col, Card, Rate, Spin, Empty, Typography } from 'antd';
import {
  ShopOutlined,
  DollarOutlined,
  StarOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { providersAPI } from '../api/services';

const { Text } = Typography;

const statusColors = {
  pending: 'orange',
  confirmed: 'blue',
  cancelled: 'red',
  completed: 'green',
  initiated: 'default',
  paid: 'green',
  failed: 'red',
  refunded: 'purple',
};

const DAY_NAMES = ['Dushanba', 'Seshanba', 'Chorshanba', 'Payshanba', 'Juma', 'Shanba', 'Yakshanba'];
const DAY_SHORT = ['Du', 'Se', 'Cho', 'Pa', 'Ju', 'Sha', 'Ya'];

const formatTime = (t) => {
  if (!t) return '--:--';
  const str = String(t);
  if (str.includes(':')) return str.slice(0, 5);
  return str;
};

const ProviderDetailModal = ({ open, onClose, provider, allServices, allBookings, allPayments, allReviews, allSlots }) => {
  const [loading, setLoading] = useState(false);
  const [providerDetail, setProviderDetail] = useState(null);

  useEffect(() => {
    if (open && provider) {
      setLoading(true);
      providersAPI.getOne(provider.id)
        .then(res => setProviderDetail(res.data?.data || res.data))
        .catch(() => setProviderDetail(provider))
        .finally(() => setLoading(false));
    }
  }, [open, provider]);

  if (!provider) return null;

  const prov = providerDetail || provider;

  // Filter related data by provider_id
  const providerServices = (allServices || []).filter(s => s.provider_id === prov.id);
  const serviceIds = providerServices.map(s => s.id);
  const providerBookings = (allBookings || []).filter(b => b.provider_id === prov.id || serviceIds.includes(b.service_id));
  const providerPayments = (allPayments || []).filter(p => {
    const booking = providerBookings.find(b => b.id === p.booking_id);
    return !!booking;
  });
  const providerReviews = (allReviews || []).filter(r => serviceIds.includes(r.service_id));

  // Availability slots for this provider
  const providerSlots = (allSlots || []).filter(
    s => s.provider_id === prov.id || serviceIds.includes(s.service_id)
  );
  const slotsByDay = {};
  for (let d = 0; d < 7; d++) slotsByDay[d] = [];
  providerSlots.forEach(s => {
    const day = Number(s.day);
    if (day >= 0 && day <= 6) {
      slotsByDay[day].push(s);
    }
  });
  // Sort each day's slots by start time
  Object.keys(slotsByDay).forEach(d => {
    slotsByDay[d].sort((a, b) => String(a.start_datetime).localeCompare(String(b.start_datetime)));
  });
  const filledDays = Object.values(slotsByDay).filter(arr => arr.length > 0).length;
  const totalSlots = providerSlots.length;

  // Financial calculations
  const totalRevenue = providerBookings.reduce((sum, b) => sum + Number(b.total_amount || 0), 0);
  const totalPaid = providerPayments.filter(p => p.status === 'paid').reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const pendingPayments = providerPayments.filter(p => p.status === 'initiated').reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const completedBookings = providerBookings.filter(b => b.status === 'completed').length;
  const cancelledBookings = providerBookings.filter(b => b.status === 'cancelled').length;
  const avgRating = providerReviews.length > 0
    ? (providerReviews.reduce((sum, r) => sum + Number(r.rating || 0), 0) / providerReviews.length).toFixed(1)
    : 0;

  const bookingColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 50 },
    { title: 'Xizmat ID', dataIndex: 'service_id', key: 'service_id', width: 80 },
    {
      title: 'Summa',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (v) => <Text strong>{Number(v || 0).toLocaleString()} UZS</Text>,
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag color={statusColors[s]}>{s?.toUpperCase()}</Tag>,
    },
    {
      title: 'Sana',
      key: 'date',
      render: (_, r) => `${r.day || '-'}/${r.month || '-'}/${r.year || '-'}`,
    },
  ];

  const paymentColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 50 },
    { title: 'Buyurtma ID', dataIndex: 'booking_id', key: 'booking_id', width: 90 },
    {
      title: 'Summa',
      dataIndex: 'amount',
      key: 'amount',
      render: (v) => <Text strong>{Number(v || 0).toLocaleString()} UZS</Text>,
    },
    { title: 'Usul', dataIndex: 'method', key: 'method', render: (v) => v || '-' },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag color={statusColors[s]}>{s?.toUpperCase()}</Tag>,
    },
    {
      title: 'Sana',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (d) => d ? new Date(d).toLocaleDateString('uz-UZ') : '-',
    },
  ];

  const serviceColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 50 },
    { title: 'Nomi', dataIndex: 'title', key: 'title', ellipsis: true },
    {
      title: 'Narxi',
      dataIndex: 'price',
      key: 'price',
      render: (v) => `${Number(v || 0).toLocaleString()} UZS`,
    },
    { title: "Sig'im", dataIndex: 'sigim', key: 'sigim', render: (v) => v || '-' },
  ];

  const reviewColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 50 },
    {
      title: 'Reyting',
      dataIndex: 'rating',
      key: 'rating',
      render: (v) => <Rate disabled defaultValue={v} style={{ fontSize: 12 }} />,
    },
    { title: 'Izoh', dataIndex: 'comment', key: 'comment', ellipsis: true, render: (v) => v || '-' },
    {
      title: 'Sana',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (d) => d ? new Date(d).toLocaleDateString('uz-UZ') : '-',
    },
  ];

  const tabItems = [
    {
      key: 'overview',
      label: 'Umumiy',
      children: (
        <div>
          <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
            <Col xs={12} sm={8}>
              <Card size="small" style={{ textAlign: 'center', borderRadius: 10 }}>
                <Statistic
                  title={<span style={{ fontSize: 12 }}>Jami daromad</span>}
                  value={totalRevenue}
                  suffix="UZS"
                  valueStyle={{ color: '#52c41a', fontSize: 18, fontWeight: 700 }}
                  prefix={<DollarOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card size="small" style={{ textAlign: 'center', borderRadius: 10 }}>
                <Statistic
                  title={<span style={{ fontSize: 12 }}>To'langan</span>}
                  value={totalPaid}
                  suffix="UZS"
                  valueStyle={{ color: '#1677ff', fontSize: 18, fontWeight: 700 }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card size="small" style={{ textAlign: 'center', borderRadius: 10 }}>
                <Statistic
                  title={<span style={{ fontSize: 12 }}>Kutilayotgan</span>}
                  value={pendingPayments}
                  suffix="UZS"
                  valueStyle={{ color: '#fa8c16', fontSize: 18, fontWeight: 700 }}
                  prefix={<ClockCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card size="small" style={{ textAlign: 'center', borderRadius: 10 }}>
                <Statistic
                  title={<span style={{ fontSize: 12 }}>Yakunlangan</span>}
                  value={completedBookings}
                  valueStyle={{ color: '#52c41a', fontSize: 18, fontWeight: 700 }}
                  prefix={<CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card size="small" style={{ textAlign: 'center', borderRadius: 10 }}>
                <Statistic
                  title={<span style={{ fontSize: 12 }}>Bekor qilingan</span>}
                  value={cancelledBookings}
                  valueStyle={{ color: '#ff4d4f', fontSize: 18, fontWeight: 700 }}
                  prefix={<CloseCircleOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card size="small" style={{ textAlign: 'center', borderRadius: 10 }}>
                <Statistic
                  title={<span style={{ fontSize: 12 }}>O'rtacha reyting</span>}
                  value={avgRating}
                  suffix={`/ 5 (${providerReviews.length})`}
                  valueStyle={{ color: '#faad14', fontSize: 18, fontWeight: 700 }}
                  prefix={<StarOutlined />}
                />
              </Card>
            </Col>
          </Row>

          <Descriptions
            bordered
            size="small"
            column={{ xs: 1, sm: 2 }}
            style={{ marginTop: 8 }}
          >
            <Descriptions.Item label={<><ShopOutlined /> Nomi</>}>{prov.name}</Descriptions.Item>
            <Descriptions.Item label={<><PhoneOutlined /> Telefon</>}>{prov.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label={<><EnvironmentOutlined /> Manzil</>} span={2}>{prov.address || '-'}</Descriptions.Item>
            <Descriptions.Item label="Tavsif" span={2}>{prov.description || '-'}</Descriptions.Item>
            <Descriptions.Item label="STIR">{prov.stir || '-'}</Descriptions.Item>
            <Descriptions.Item label={<><GlobalOutlined /> Veb-sayt</>}>{prov.website || '-'}</Descriptions.Item>
            <Descriptions.Item label="Viloyat ID">{prov.region_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="Tuman ID">{prov.district_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="Koordinatalar">
              {prov.latitude && prov.longitude ? `${prov.latitude}, ${prov.longitude}` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Yaratilgan">
              {prov.created_at ? new Date(prov.created_at).toLocaleString('uz-UZ') : '-'}
            </Descriptions.Item>
          </Descriptions>
        </div>
      ),
    },
    {
      key: 'services',
      label: `Xizmatlar (${providerServices.length})`,
      children: providerServices.length > 0 ? (
        <Table dataSource={providerServices} columns={serviceColumns} rowKey="id" size="small" pagination={{ pageSize: 5 }} scroll={{ x: 400 }} />
      ) : <Empty description="Xizmatlar topilmadi" />,
    },
    {
      key: 'bookings',
      label: `Buyurtmalar (${providerBookings.length})`,
      children: providerBookings.length > 0 ? (
        <Table dataSource={providerBookings} columns={bookingColumns} rowKey="id" size="small" pagination={{ pageSize: 5 }} scroll={{ x: 500 }} />
      ) : <Empty description="Buyurtmalar topilmadi" />,
    },
    {
      key: 'payments',
      label: `To'lovlar (${providerPayments.length})`,
      children: providerPayments.length > 0 ? (
        <Table dataSource={providerPayments} columns={paymentColumns} rowKey="id" size="small" pagination={{ pageSize: 5 }} scroll={{ x: 600 }} />
      ) : <Empty description="To'lovlar topilmadi" />,
    },
    {
      key: 'slots',
      label: (
        <span>
          <CalendarOutlined style={{ marginRight: 4 }} />
          Bandlik ({filledDays}/7)
        </span>
      ),
      children: (
        <div>
          {/* Summary bar */}
          <div style={{
            display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap',
            padding: '12px 16px', background: '#fafafa', borderRadius: 10,
          }}>
            <div style={{ flex: 1, minWidth: 100, textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: filledDays >= 7 ? '#22c55e' : filledDays > 0 ? '#f59e0b' : '#ef4444' }}>
                {filledDays}/7
              </div>
              <div style={{ fontSize: 11, color: '#999' }}>Kun band</div>
            </div>
            <div style={{ flex: 1, minWidth: 100, textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#4f46e5' }}>{totalSlots}</div>
              <div style={{ fontSize: 11, color: '#999' }}>Jami slotlar</div>
            </div>
            <div style={{ flex: 1, minWidth: 100, textAlign: 'center' }}>
              <Tag
                color={filledDays >= 7 ? 'success' : filledDays > 0 ? 'warning' : 'error'}
                style={{ fontSize: 13, padding: '2px 12px', fontWeight: 600, marginTop: 2 }}
              >
                {filledDays >= 7 ? "To'liq band" : filledDays > 0 ? 'Qisman band' : "Bo'sh"}
              </Tag>
            </div>
          </div>

          {/* Weekly calendar grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
            {[0, 1, 2, 3, 4, 5, 6].map(day => {
              const daySlots = slotsByDay[day] || [];
              const hasSlotsForDay = daySlots.length > 0;
              return (
                <div
                  key={day}
                  style={{
                    borderRadius: 10,
                    border: `1.5px solid ${hasSlotsForDay ? '#22c55e' : '#f0f0f0'}`,
                    background: hasSlotsForDay ? '#f0fdf4' : '#fafafa',
                    padding: '8px 6px',
                    textAlign: 'center',
                    minHeight: 100,
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{
                    fontWeight: 700, fontSize: 12, marginBottom: 6,
                    color: hasSlotsForDay ? '#16a34a' : '#bbb',
                    textTransform: 'uppercase', letterSpacing: 0.5,
                  }}>
                    {DAY_SHORT[day]}
                  </div>
                  <div style={{
                    fontSize: 10, color: '#999', marginBottom: 6,
                  }}>
                    {DAY_NAMES[day]}
                  </div>
                  {hasSlotsForDay ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {daySlots.map((slot, idx) => (
                        <div
                          key={slot.id || idx}
                          style={{
                            background: '#22c55e15',
                            borderRadius: 6,
                            padding: '3px 4px',
                            fontSize: 10,
                            color: '#16a34a',
                            fontWeight: 600,
                            lineHeight: 1.3,
                          }}
                        >
                          {formatTime(slot.start_datetime)}
                          <span style={{ color: '#999', margin: '0 1px' }}>-</span>
                          {formatTime(slot.end_datetime)}
                          {slot.guescount ? (
                            <div style={{ fontSize: 9, color: '#888', fontWeight: 400 }}>
                              {slot.guescount} kishi
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      color: '#d9d9d9', fontSize: 20, marginTop: 8,
                    }}>
                      —
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Detailed list below */}
          {totalSlots > 0 && (
            <div style={{ marginTop: 16 }}>
              <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
                Batafsil ro'yxat:
              </Text>
              <Table
                dataSource={providerSlots}
                rowKey={(r) => r.id || `${r.day}-${r.start_datetime}`}
                size="small"
                pagination={{ pageSize: 7 }}
                scroll={{ x: 450 }}
                columns={[
                  {
                    title: 'Kun',
                    dataIndex: 'day',
                    key: 'day',
                    width: 90,
                    render: (d) => (
                      <Tag color="blue" style={{ margin: 0 }}>{DAY_NAMES[Number(d)] || d}</Tag>
                    ),
                    sorter: (a, b) => Number(a.day) - Number(b.day),
                  },
                  {
                    title: 'Boshlanish',
                    dataIndex: 'start_datetime',
                    key: 'start',
                    width: 80,
                    render: (v) => <Text strong>{formatTime(v)}</Text>,
                  },
                  {
                    title: 'Tugash',
                    dataIndex: 'end_datetime',
                    key: 'end',
                    width: 80,
                    render: (v) => <Text strong>{formatTime(v)}</Text>,
                  },
                  {
                    title: 'Kishilar',
                    dataIndex: 'guescount',
                    key: 'guescount',
                    width: 70,
                    render: (v) => v || '-',
                  },
                  {
                    title: 'Izoh',
                    dataIndex: 'note',
                    key: 'note',
                    ellipsis: true,
                    render: (v) => v || '-',
                  },
                ]}
              />
            </div>
          )}
          {totalSlots === 0 && <Empty description="Bandlik slotlari topilmadi" style={{ marginTop: 16 }} />}
        </div>
      ),
    },
    {
      key: 'reviews',
      label: `Sharhlar (${providerReviews.length})`,
      children: providerReviews.length > 0 ? (
        <Table dataSource={providerReviews} columns={reviewColumns} rowKey="id" size="small" pagination={{ pageSize: 5 }} scroll={{ x: 400 }} />
      ) : <Empty description="Sharhlar topilmadi" />,
    },
  ];

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: '#4f46e515', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: '#4f46e5', fontSize: 18,
          }}>
            <ShopOutlined />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>{prov.name}</div>
            <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 400 }}>
              {prov.address || 'Manzil ko\'rsatilmagan'}
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={850}
      styles={{ body: { padding: '12px 24px 24px', maxHeight: '70vh', overflowY: 'auto' } }}
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
      ) : (
        <Tabs items={tabItems} size="small" />
      )}
    </Modal>
  );
};

export default ProviderDetailModal;
